import { useRef, useState, useCallback, useEffect } from 'react'
import { Box, Grid, IconButton } from '@mui/material'
import FormCreator from '@/shared/FormCreator'
import Table from '@/shared/Table'
import { useTranslation } from 'react-i18next'
import ViewPhishCampignDialog from '@/components/ViewPhishCampignDialog'
import { connect, useSelector } from 'react-redux'
import usePermissions from '@/hooks/usePermissions'
import fieldToSortStr from '@/libs/utils/fieldToSortStr'
import get from 'lodash/get'
import Typography from '@mui/material/Typography'
import toUpper from 'lodash/toUpper'
import dayjs from 'dayjs'
import debounce from '@mui/material/utils/debounce'
import unionBy from 'lodash/unionBy'
import Preview from '@mui/icons-material/Preview'
import moment from 'moment'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'


const mapStateToProps = ({ phishCampaign, loading }) => ({
    phishCampaignList: phishCampaign.phishCampaignList,
    isLoading: loading['phishCampaign/getPhishCampaigns'] || loading['phishCampaign/getPhishCampaignChildren'] || loading['phishCampaign/getPhishCampaignChildDetails'] || loading['phishCampaign/getPhishCampaignDetails'],
    phishCampaignChildren: phishCampaign.phishCampaignChildren,
    phishCampaignDetails: phishCampaign.phishCampaignDetails,
    phishCampaignChildDetails: phishCampaign.phishCampaignChildDetails,
})

const fieldMap = {
    campaignMainName: 'CAMP_NAME_MAIN',
    companyName: 'COMP_NAME',
    role: 'ROLE',
    phone: 'PHONE',
    status: 'STATUS'
}

const ActivityList = ({ dispatch, phishCampaignList, isLoading, phishCampaignChildren, phishCampaignDetails, phishCampaignChildDetails }) => {

    const form = useRef()
    const { t } = useTranslation()
    const { data, paging = {} } = phishCampaignList || {}
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const companyPageRef = useRef(0)
    const [companyResults, setCompanyResults] = useState({})
    const { data: companyResultsData = [] } = companyResults
    const [editUserData, setEditUserData] = useState()
    const [filterDateRange, setFilterDateRange] = useState()
    const [sort, setSort] = useState([{ field: 'createdTime', sort: 'desc' }])
    const [runTypeOpt, setRunTypeOpt] = useState(['main', 'pre'])
    const statusOptions = [
        { value: 'ALL', label: t('status.ALL') },
        { value: 'NO_START', label: t('status.NS') },
        { value: 'IN_PROGRESS', label: t('status.IP') },
        { value: 'COMPLETE', label: t('status.COM') },
        { value: 'CANCEL', label: t('status.CAN') },
    ]

    const [selectedCampaign, setSelectedCampaign] = useState(null);


    const [expandedId, setExpandedId] = useState()
    const selectedStatus = form.current?.getValues()?.status || null

    const accountId = useSelector(state => state.auth.account?.accountId)

    const canView = usePermissions('emailCampaign_view')

    const btnProps = {
        size: 'midle',
        sx: { borderRadius: 2, padding: 0, color: theme => theme.palette.secondary_80.main },
    }

    const iconProps = {
        sx: {
            padding: 0.3,
        },
    }

    const pagination = {
        ...paging,
        page,
        pageSize,
        onPageChange: setPage,
        onPageSizeChange: setPageSize,
    }

    const ChildrenComponent = ({ mainId, valueFormatter, render }) => {
        if (mainId?.toString() === expandedId?.toString()) {
            return (
                <Stack height="100%" width="100%">
                    {phishCampaignChildren?.[mainId]?.map((child, index) => (
                        <Box
                            key={`${mainId}_child_${index + 1}`}
                            p={1}
                            width="100%"
                            color={theme => theme.palette.accent_60.main}
                        >
                            {render?.(child) || (
                                <Typography
                                    pl={2}
                                    sx={{ width: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}
                                >
                                    {valueFormatter?.(child) || '-'}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Stack>
            )
        }

        return null
    }

    const fetchData = useCallback(() => {
        if (filterDateRange) {

            const values = form.current?.getValues() || {}

            // 從 form 拿多欄位條件
            const payload = {
                searchCampInitialTime: moment(filterDateRange.startDate).toDate(),
                searchCampEndTime: moment(filterDateRange.endDate).toDate(),
                pageIndex: page,
                pageSize,
                sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
                searchCampName: values?.campaignMainName?.trim() || null,
                searchCompanyName: values?.company?.label || null,
                searchStatus: values?.status?.value || null,
                runType: runTypeOpt.join(','),
            }

            // const payload = {
            //     searchCampInitialTime: moment(filterDateRange.startDate).toDate(),
            //     searchCampEndTime: moment(filterDateRange.endDate).toDate(),
            //     searchCampName: search,
            //     searchCompanyName,
            //     sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
            //     searchStatus,
            //     runType: runTypeOpt.join(','),
            //     pageIndex: page,
            //     pageSize,
            // }

            dispatch({
                type: 'phishCampaign/getPhishCampaigns',
                payload,
            })

            dispatch({
                type: 'phishCampaign/getPhishCampaignsStatusTotal',
            })
        }
    }, [filterDateRange, sort, runTypeOpt, page, pageSize, dispatch])

    const fetchChildren = id => {
        const payload = {
            searchCampInitialTime: moment(filterDateRange.startDate).toDate(),
            searchCampEndTime: moment(filterDateRange.endDate).toDate(),
            sort: fieldToSortStr(get(sort, '[0]'), fieldMap),
            pageIndex: 0,
            pageSize: 10,
            campaignId: id,
        }

        dispatch({
            type: 'phishCampaign/getPhishCampaignChildren',
            payload,
        })
    }

    const handleExpand = d => {
        const { campaignMainId: expandId } = d
        if (expandId === expandedId) {
            setExpandedId()
        } else {
            setExpandedId(expandId)
            fetchChildren(expandId)
        }
    }

    const cols = [
        {
            field: 'campaignMainName',
            headerName: t('general.campaign_name'),
            width: 300,
            renderCell: ({ value, row }) => (
                <Stack width={'100%'}>
                    <Stack direction={'row'}>

                        <Stack width={'100%'} flexDirection={'row'} spacing={1} alignItems={'center'}>
                            <Tooltip title={value} placement="top-end">
                                <Typography
                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {value}
                                </Typography>
                            </Tooltip>
                        </Stack>

                        {row.children === 1 && (
                            <IconButton
                                component="div"
                                size="small"
                                sx={{ marginTop: '0 !important' }}
                                onClick={e => {
                                    e.stopPropagation()
                                    handleExpand(row)
                                }}
                            >
                                {row.campaignMainId.toString() === expandedId?.toString() ? (
                                    <ArrowDropUp />
                                ) : (
                                    <ArrowDropDown />
                                )}
                            </IconButton>
                        )}
                    </Stack>

                    <ChildrenComponent
                        mainId={row.campaignMainId}
                        render={child => (

                            <Tooltip title={child.campaignName} placement="top-end">
                                <Typography
                                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {child.campaignName}
                                </Typography>
                            </Tooltip>
                        )}
                    />
                </Stack>
            ),
        },
        {
            field: 'companyName', headerName: t('general.company_name'), width: 250,
            renderCell: ({ value, row }) => (
                <Stack height="100%" width={'100%'}>
                    <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t(`${value}`)}
                    </Typography>

                    <ChildrenComponent
                        mainId={row.campaignMainId}
                        valueFormatter={() => t(`${value}`)}
                    />
                </Stack>
            ),
        },
        {
            field: 'status', headerName: t('general.status'), width: 150, valueFormatter: ({ value }) => (value ? t(`status.${toUpper(value)}`) : '-'),
            renderCell: ({ value, row }) => (
                <Stack height="100%" width={'100%'}>
                    <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t(`status.${value}`)}
                    </Typography>

                    <ChildrenComponent
                        mainId={row.campaignMainId}
                        valueFormatter={(child) => t(`status.${child?.status}`)}
                    />

                </Stack>
            ),
        },
        {
            field: 'period',
            headerName: t('general.campaign_period'),
            width: 350,

            valueGetter: ({ row }) => {
                const { startTime, endTime } = row || {}
                if (!startTime || !endTime) return '-'
                return `${dayjs(startTime).format('YYYY/MM/DD')} ~ ${dayjs(endTime).format('YYYY/MM/DD')}`
            },

            renderCell: ({ row }) => (
                <Stack height="100%" width="100%">
                    <Typography p={1} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row?.startTime && row?.endTime
                            ? `${moment(row.startTime).format(DATE_TIME_FORMAT)} ~ ${moment(row.endTime).format(DATE_TIME_FORMAT)}`
                            : '-'}
                    </Typography>

                    <ChildrenComponent
                        mainId={row.campaignMainId}
                        valueFormatter={(child) => {
                            if (!child?.startTime || !child?.endTime) return '-'
                            return `${moment(child.startTime).format(DATE_TIME_FORMAT)} ~ ${moment(child.endTime).format(DATE_TIME_FORMAT)}`
                        }}
                    />
                </Stack>
            ),
        },
        {
            field: 'preview',
            hidden: !canView,
            headerName: t('general.view_campaign_content'),
            minWidth: 200,
            renderCell: ({ row }) => (
                <Stack height="100%" width="100%">
                    <IconButton
                        sx={{
                            mt: 0.1,
                            mb: 0,
                            borderRadius: 2,
                            color: theme => theme.palette.secondary_80.main
                        }}
                        // onClick={() => setEditUserData(row)}
                        onClick={() => {
                            if (!row) return;

                            setSelectedCampaign({ type: 'main', id: row.campaignMainId });

                            dispatch({
                                type: 'phishCampaign/getPhishCampaignDetails',
                                payload: { campaignId: row.campaignMainId },
                            });
                        }}
                    >
                        <Preview {...iconProps} />
                    </IconButton>

                    <ChildrenComponent
                        mainId={row.campaignMainId}
                        render={child => (
                            <Box>
                                <IconButton
                                    // {...btnProps}
                                    sx={{
                                        ...btnProps.sx,
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    onClick={() => {
                                        if (!child) return;

                                        setSelectedCampaign({ type: 'child', id: child.campaignId });

                                        dispatch({
                                            type: 'phishCampaign/getPhishCampaignChildDetails',
                                            payload: { campaignId: child.campaignId },
                                        });
                                    }}
                                >
                                    <Preview {...iconProps} />
                                </IconButton>
                            </Box>
                        )
                        }
                    />
                </Stack >
            )
        }
    ]

    const getCompany = compinfo => {
        const { companyPage = 0, keyword = '' } = compinfo || {}

        companyPageRef.current = companyPage

        dispatch({
            type: 'account/getCompany',
            payload: {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword, //  關鍵字
            },
            successCallback: response => {
                const { data: responseData = [], paging: responsePaging = {} } = response || {}
                const { pageIndex } = responsePaging

                const newData = responseData.map(d => ({
                    value: d.companyId,
                    label: d.companyName,
                }))

                setCompanyResults(s => ({
                    data: pageIndex > 0 ? unionBy(s.data || [], newData, 'value') : newData,
                    paging: responsePaging,
                }))
            },
        })
    }

    const fetchCompanyData = useCallback(
        companyNameValue => {
            const keyword = companyNameValue || form.current?.getValues()?.companyName?.label
            const payload = {
                pageIndex: companyPageRef.current,
                pageSize: 10,
                keyword: keyword || '', // 空值也可以發送
            }

            dispatch({
                type: 'account/getCompany',
                payload,
                successCallback: response => {
                    const { data: responseData = [], paging: responsePaging = {} } = response || {}
                    const { pageIndex } = responsePaging

                    const newData = responseData.map(d => ({
                        value: d.companyId,
                        label: d.companyName,
                    }))

                    setCompanyResults(s => ({
                        data: pageIndex > 0
                            ? unionBy(s.data || [], newData, 'value')
                            : newData,
                        paging: responsePaging,
                    }))
                },
            })
        },
        [dispatch],
    )

    const setCompanyNameDebounce = debounce((_, value) => {
        companyPageRef.current = 0
        fetchCompanyData(value)
    }, 400)

    useEffect(() => {
        const monthValue = Math.floor(6 / 2)
        const remainder = (6 / 2) % 1
        const dayValue = 30 * remainder

        const startDate = moment().subtract(monthValue, 'month').subtract(dayValue, 'day').toDate()
        const endDate = moment().add(monthValue, 'month').add(dayValue, 'day').toDate()

        form.current?.setValue?.('dateRangeFilter', {
            startDate,
            endDate,
        })

        setFilterDateRange({
            startDate,
            endDate,
        })

    }, [])

    useEffect(() => {
        if (!accountId) return
        fetchData()
        getCompany()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, fetchData, page, pageSize]);

    useEffect(() => {
        if (!selectedCampaign) return;

        const { type, id } = selectedCampaign;

        if (type === 'main') {
            if (!phishCampaignDetails) return;
            if (phishCampaignDetails.campaignMainId?.toString() !== id?.toString()) return;

            setEditUserData(phishCampaignDetails);
            setSelectedCampaign(null); // 清掉 pending 狀態
        }

        if (type === 'child') {
            if (!phishCampaignChildDetails) return;
            if (phishCampaignChildDetails.campaignId?.toString() !== id?.toString()) return;

            setEditUserData(phishCampaignChildDetails);
            setSelectedCampaign(null); // 清掉 pending 狀態
        }
    }, [selectedCampaign, phishCampaignDetails, phishCampaignChildDetails]);


    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 520,
                    gap: 2,
                    border: '1px solid #2D2D6C',
                    borderRadius: 3,
                    overflow: 'hidden',
                    p: 2,
                    backgroundColor: '#fff',
                }}
            >
                {/* 搜尋區 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        // mb: 0.5,
                        alignItems: 'center',
                    }}
                >
                    <Grid item>
                        <FormCreator
                            ref={form}
                            formItems={[
                                {
                                    name: 'campaignMainName',
                                    placeholder: t('general.insert_campaign_name'),
                                    defaultValue: '',
                                    inputConfig: {
                                        onSubmit: async () => {
                                            setPage(0)
                                            fetchData()
                                        }
                                    },
                                },
                                {
                                    label: t('general.custom_date_range'),
                                    name: 'dateRangeFilter',
                                    type: 'dateRange',
                                    defaultValue: { startDate: new Date(), endDate: new Date() },
                                    inputConfig: {
                                        onChange: value => {
                                            setFilterDateRange(value)
                                        },
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'company',
                                    defaultValue: null,
                                    inputConfig: {
                                        options: companyResultsData,
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('company', selectedOption || null)
                                            form.current.setValue('companyName', selectedOption?.label || '')
                                            companyPageRef.current = 0
                                            fetchData()
                                        },
                                        onInputChange: (_, inputValue) => {
                                            setCompanyNameDebounce(_, inputValue) //  debounce 處理 keyword
                                        },
                                        placeholder: t('general.company_name'),
                                    },
                                },
                                {
                                    type: 'autocomplete',
                                    name: 'status',
                                    defaultValue: null,
                                    inputConfig: {
                                        options: statusOptions,
                                        value: selectedStatus, //  這裡指定目前選中值
                                        isOptionEqualToValue: (option, value) => option?.value === value?.value,
                                        onChange: selectedOption => {
                                            form.current.setValue('status', selectedOption || null)
                                            form.current.setValue('statusName', selectedOption?.label || '')

                                            fetchData()
                                        },
                                        placeholder: t('general.status'),
                                    },
                                },
                                {
                                    label: t('general.campaign_run_type'),
                                    name: 'setRunTypeOpt',
                                    type: 'checkboxList',
                                    defaultValue: ['main', 'pre'],
                                    inputConfig: {
                                        onChange: val => {
                                            setRunTypeOpt(val)
                                        },
                                        options: [
                                            { value: 'main', label: t('general.run_type_main') },
                                            { value: 'pre', label: t('general.run_type_pre') },
                                            { value: 'test', label: t('general.run_type_test') },
                                        ],
                                    },
                                },

                            ]}
                            flexDirection="row"
                            submitComponent={() => null}
                            resetComponent={() => null}
                            gridItemProps={{ mr: 3 }}
                        />
                    </Grid>
                </Box>

                {/* 表格區 */}
                <Table
                    minHeight={430}
                    columns={cols}
                    rows={data || []}
                    dataGridConfig={{
                        sortingMode: 'server',
                        sortingOrder: ['desc', 'asc', null],
                        getRowId: row => row.campaignMainId,
                        sortModel: sort,
                        onSortModelChange: newSort => setSort(newSort),
                        loading: isLoading,
                        getRowHeight: () => 'auto',
                    }}
                    noRowsLabel={t('errors.no_campaign')}
                    pagination={pagination}
                />
                {/* <Watermark username={username} /> */}
            </Box>

            {/* <CreateAccountDialog
                open={open}
                onClose={() => setOpen(false)}
                onComplete={() => {
                    fetchData()
                }}
            /> */}

            <ViewPhishCampignDialog
                open={!!editUserData}
                data={editUserData}
                onClose={() => {
                    setEditUserData()
                }}
                onComplete={() => {
                    fetchData()
                }}
            />
        </>
    )
}

export default connect(mapStateToProps)(ActivityList)